import type { FilterOption } from '@odo/shared-ui';
import type { PracticeOption } from '../services/billing';

/**
 * NAME-keyed options, for filters whose values are locked to names (the work
 * queue's URL params and saved views). Duplicate names merge their doctors
 * into one option. Prefer the id-keyed `practiceOptionsFromList` elsewhere.
 */
export function practiceNameOptionsFromList(practices: readonly PracticeOption[]): FilterOption[] {
    const doctorsByName = new Map<string, string[]>();
    for (const p of practices) {
        const existing = doctorsByName.get(p.name) ?? [];
        const doctor = p.doctorName ? `Dr. ${p.doctorName}` : null;
        doctorsByName.set(
            p.name,
            doctor && !existing.includes(doctor) ? [...existing, doctor] : existing
        );
    }
    return [...doctorsByName.entries()].map(([name, doctors]) =>
        doctors.length === 0
            ? name
            : { value: name, label: name, sublabel: doctors.join(' · '), keywords: doctors }
    );
}

/**
 * Same option shape, keyed off the id-based `PracticeOption` list (billing's
 * `usePracticeOptions`) instead of names - shared by every id-keyed practice
 * picker/filter (cross-practice Calls toolbar, the practice-less call form's
 * Practice picker) so the "name + Dr. X sublabel" shaping lives in one place.
 */
export function practiceOptionsFromList(practices: readonly PracticeOption[]): FilterOption[] {
    return practices.map((p) =>
        p.doctorName
            ? {
                  value: p.id,
                  label: p.name,
                  sublabel: `Dr. ${p.doctorName}`,
                  keywords: [`Dr. ${p.doctorName}`, p.doctorName]
              }
            : { value: p.id, label: p.name }
    );
}
